import React, { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../hooks";
import { useAppDispatch } from "../../redux/store";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout/slice";
import { getOpenShifts, claimOpenShifts } from "../../redux/features/openShift";
import { OpenShift } from "../../types/model/openShift";
import { RootState } from "../../redux/store";
import { GetOpenShiftsRequest, ClaimOpenShiftRequest } from "../../types/request";

const OpenShiftsSelection: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    
    // Get 15 days from today as default date range
    const now = new Date();
    const startDate15Days = new Date(now);
    const endDate15Days = new Date(now);
    endDate15Days.setDate(now.getDate() + 15);
    
    const [startDate, setStartDate] = useState(startDate15Days.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(endDate15Days.toISOString().split('T')[0]);
    const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
    const [notes, setNotes] = useState("");

    // Get open shifts from Redux store
    const { openShifts, isLoadingOpenShifts, isCreatingOpenShift, error } = useSelector((state: RootState) => state.openShift);

    useEffect(() => {
        // Set up global back handler for mobile header
        setGlobalBackHandler(() => {
            navigate(-1);
            return true;
        });

        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [navigate]);

    // Helper function to format date as DD-MM-YYYY
    const formatDateForAPI = (dateString: string) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    useEffect(() => {
        // Fetch open shifts when component mounts with date range
        const dateRange: GetOpenShiftsRequest = {
            start_date: formatDateForAPI(startDate),
            end_date: formatDateForAPI(endDate)
        };
        dispatch(getOpenShifts(dateRange));
    }, [dispatch, startDate, endDate]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleShiftSelect = (shiftId: string) => {
        setSelectedShiftIds(prev => {
            if (prev.includes(shiftId)) {
                return prev.filter(id => id !== shiftId);
            } else {
                return [...prev, shiftId];
            }
        });
    };

    const handleClaimShifts = async () => {
        if (selectedShiftIds.length === 0) {
            toast.error("Please select at least one shift to claim.");
            return;
        }
        
        const claimData: ClaimOpenShiftRequest = {
            shift_ids: selectedShiftIds,
            notes: notes.trim() || undefined
        };
        
        dispatch(claimOpenShifts(claimData)).unwrap().then((response) => {
            if(response) {
                toast.success(response);
            } else {
                toast.success(`Successfully claimed ${selectedShiftIds.length} shift${selectedShiftIds.length > 1 ? 's' : ''}!`);
            }
        }).catch((error) => {
            toast.error(error || "Failed to claim shifts. Please try again.");
        });
        
        // Clear selections and notes after successful claim
        setSelectedShiftIds([]);
        setNotes("");        
    };

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Pick an Open Shift"));
    }, [dispatch]);

    // Helper function to format date
    const formatDate = (dateString?: Date) => {
        if (!dateString) return "No date";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Helper function to format time
    const formatTime = (startTime?: string, endTime?: string) => {
        if (!startTime || !endTime) return "No time specified";
        return `${startTime} – ${endTime}`;
    };

    const ShiftCard: React.FC<{ shift: OpenShift; isSelected: boolean }> = ({ shift, isSelected }) => (
        <div
            className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                isSelected
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            }`}
            onClick={() => handleShiftSelect(shift.os_sch_open_shift_transactions || '')}
        >
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {shift.shift_details?.shift_name || "Unnamed Shift"}
                    </h3>
                    <p className="text-gray-600 mb-2">{formatDate(shift.shift_date ? new Date(shift.shift_date) : undefined)}</p>
                    <div className="flex items-center mb-2">
                        {isSelected && (
                            <div className="flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full mr-2">
                                <Check className="w-4 h-4 text-white" />
                            </div>
                        )}
                        <span className="text-lg font-medium text-gray-900">
                            {formatTime(shift.shift_details?.shift_start_time, shift.shift_details?.shift_end_time)}
                        </span>
                    </div>
                    {shift.shift_details?.shift_code && (
                        <p className="text-gray-500 text-sm">Code: {shift.shift_details.shift_code}</p>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Web Header - Hidden on mobile since mobileHeader is automatically included */}
            <div className="hidden md:block bg-white shadow-sm border-b">
                <div className="w-1/2 mx-auto px-4 py-4">
                    <div className="flex items-center">
                        <button
                            onClick={handleBack}
                            className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Go back"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <h1 className="text-xl font-semibold text-gray-900">
                            Pick an Open Shift
                        </h1>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="min-h-screen bg-gray-50 md:bg-white">
                <div className="w-full md:w-1/2 md:mx-auto p-4 md:p-6">

                    {/* Date Range Filters */}
                    <div className="mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label
                                    htmlFor="startDate"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Start Date
                                </label>    
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="endDate"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoadingOpenShifts && (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <p className="text-red-600">Error loading open shifts: {error}</p>
                        </div>
                    )}

                    {/* Shift Cards */}
                    {!isLoadingOpenShifts && !error && (
                        <div className={`border border-gray-200 rounded-lg p-4 ${
                            openShifts.length > 3 ? 'h-96 overflow-y-auto' : ''
                        }`}>
                            {openShifts.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No open shifts available for the selected date range.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {openShifts.map((shift) => (
                                        <ShiftCard
                                            key={shift.os_sch_open_shift_transactions}
                                            shift={shift}
                                            isSelected={selectedShiftIds.includes(shift.os_sch_open_shift_transactions || '')}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Notes Section - Always shown */}
                    <div className="mt-6">
                        <label
                            htmlFor="shiftNotes"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Notes (Optional)
                        </label>
                        <textarea
                            id="shiftNotes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                            placeholder="Add any additional information or special requirements..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            This information will be shared with your supervisor
                        </p>
                    </div>

                    {/* Action Button - Always shown */}
                    <div className="mt-6 sticky bottom-4 md:static">
                        <button 
                            onClick={handleClaimShifts}
                            className={`w-full font-medium py-3 px-4 rounded-lg transition-colors ${
                                selectedShiftIds.length > 0 && !isCreatingOpenShift
                                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            }`}
                            disabled={selectedShiftIds.length === 0 || isCreatingOpenShift}
                        >
                            {isCreatingOpenShift ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Claiming...
                                </div>
                            ) : selectedShiftIds.length > 0 
                                ? `Claim ${selectedShiftIds.length} Shift${selectedShiftIds.length > 1 ? 's' : ''}`
                                : "Select shifts to claim"
                            }
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OpenShiftsSelection;
